import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronRight, FolderPlus, Pencil, Plus, Trash2, Users } from 'lucide-react';

import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { motionSafe, springDefault } from '@/lib/motion';
import { toast } from '@/lib/toast';
import { useTasks } from '@/features/tasks/useTasks';
import { ClientSheet } from './ClientSheet';
import { ProjectSheet } from './ProjectSheet';
import type { Client, Project } from './types';
import {
  useClientList,
  useDeleteClient,
  useDeleteProject,
  useProjectList,
} from './useClientAdmin';

type Editing =
  | { kind: 'client'; client: Client | null }
  | { kind: 'project'; project: Project | null; client: Client }
  | null;

export function ClientsPage() {
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const clients = useClientList(showArchived);
  const projects = useProjectList(showArchived);
  const tasks = useTasks();

  const projectsByClient = useMemo(() => {
    const map = new Map<number, Project[]>();
    for (const project of projects.data ?? []) {
      const list = map.get(project.client_id) ?? [];
      list.push(project);
      map.set(project.client_id, list);
    }
    return map;
  }, [projects.data]);

  // Open task counts, so a client row says how much is actually outstanding
  // rather than just existing.
  const openByClient = useMemo(() => {
    const map = new Map<number, number>();
    for (const task of tasks.data ?? []) {
      map.set(task.client_id, (map.get(task.client_id) ?? 0) + 1);
    }
    return map;
  }, [tasks.data]);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const showSkeleton = clients.isPending && !clients.data;
  const showHardError = clients.isError && !clients.data;

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-6">
      <header className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-title font-semibold">Clients</h1>
          <p className="mt-1 text-small text-text-secondary">
            Work is organised Client → Project → Task.
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditing({ kind: 'client', client: null })}>
          <Plus size={15} strokeWidth={1.5} aria-hidden />
          New client
        </Button>
      </header>

      <div className="mb-5">
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          aria-pressed={showArchived}
          className={cn(
            'pressable rounded-md px-3 py-1.5 text-small transition-colors duration-press',
            showArchived
              ? 'bg-accent text-text-on-accent'
              : 'bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text',
          )}
        >
          Show archived
        </button>
      </div>

      {showSkeleton && (
        <ul className="space-y-2" aria-hidden>
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-16 animate-pulse rounded-lg bg-surface-2" />
          ))}
        </ul>
      )}

      {showHardError && (
        <p role="alert" className="text-body text-danger">
          Could not load clients.{' '}
          <button type="button" className="underline" onClick={() => clients.refetch()}>
            Retry
          </button>
        </p>
      )}

      {clients.data?.length === 0 && !showSkeleton && !showHardError && (
        <EmptyState onAdd={() => setEditing({ kind: 'client', client: null })} />
      )}

      <ul className="flex flex-col gap-2">
        {clients.data?.map((client) => (
          <ClientCard
            key={client.id}
            client={client}
            projects={projectsByClient.get(client.id) ?? []}
            openTasks={openByClient.get(client.id) ?? 0}
            expanded={expanded.has(client.id)}
            onToggle={() => toggle(client.id)}
            onEdit={() => setEditing({ kind: 'client', client })}
            onAddProject={() => setEditing({ kind: 'project', project: null, client })}
            onEditProject={(project) => setEditing({ kind: 'project', project, client })}
          />
        ))}
      </ul>

      <AnimatePresence>
        {editing?.kind === 'client' && (
          <ClientSheet client={editing.client} onClose={() => setEditing(null)} />
        )}
        {editing?.kind === 'project' && (
          <ProjectSheet
            project={editing.project}
            client={editing.client}
            clients={clients.data ?? []}
            onClose={() => setEditing(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ClientCard({
  client,
  projects,
  openTasks,
  expanded,
  onToggle,
  onEdit,
  onAddProject,
  onEditProject,
}: {
  client: Client;
  projects: Project[];
  openTasks: number;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onAddProject: () => void;
  onEditProject: (project: Project) => void;
}) {
  const remove = useDeleteClient();
  const [confirming, setConfirming] = useState(false);

  const archived = client.status === 'archived';

  return (
    <li
      className={cn(
        'rounded-lg border border-border bg-surface-1',
        archived && 'opacity-60',
      )}
      style={{ borderLeft: `2px solid ${client.color}` }}
    >
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse projects' : 'Expand projects'}
          className="flex size-control shrink-0 items-center justify-center rounded text-text-tertiary hover:text-text"
        >
          <motion.span
            animate={{ rotate: expanded ? 90 : 0 }}
            transition={motionSafe(springDefault)}
            className="flex"
          >
            <ChevronRight size={15} strokeWidth={1.5} aria-hidden />
          </motion.span>
        </button>

        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 truncate text-body font-medium">
            {client.name}
            {client.is_personal && (
              <span className="rounded-md bg-surface-3 px-2 py-0.5 text-label uppercase text-text-tertiary">
                Personal
              </span>
            )}
            {archived && (
              <span className="rounded-md bg-surface-3 px-2 py-0.5 text-label uppercase text-text-tertiary">
                Archived
              </span>
            )}
          </p>
          <p className="mt-0.5 flex flex-wrap gap-3 text-small text-text-tertiary">
            <span className="tabular">{plural(projects.length, 'project')}</span>
            <span className="tabular">{openTasks} open</span>
            {client.hourly_rate && (
              <span className="tabular">
                {client.currency} {client.hourly_rate}/h
              </span>
            )}
            {client.budget_type !== 'none' && client.budget_amount && (
              <span className="tabular">
                {client.budget_amount}
                {client.budget_type === 'retainer_hours' ? 'h' : ''} {client.budget_period}
              </span>
            )}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <IconButton onClick={onAddProject} label={`Add project to ${client.name}`}>
            <FolderPlus size={15} strokeWidth={1.5} aria-hidden />
          </IconButton>
          <IconButton onClick={onEdit} label={`Edit ${client.name}`}>
            <Pencil size={15} strokeWidth={1.5} aria-hidden />
          </IconButton>
          {/* Delete stays desktop-visible; on phones it lives in Edit to keep
              the row thumb-friendly and keep destructive away from easy reach. */}
          {!client.is_personal && (
            <span className="hidden sm:inline-flex">
              <IconButton
                onClick={() => setConfirming(true)}
                label={`Delete ${client.name}`}
                danger
              >
                <Trash2 size={15} strokeWidth={1.5} aria-hidden />
              </IconButton>
            </span>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {confirming && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={motionSafe(springDefault)}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2">
              {/* Naming the cost, because this one really is irreversible:
                  deleting a client cascades to its projects and every task and
                  time entry underneath them. */}
              <p className="text-small text-danger">
                Delete {client.name}, its {plural(projects.length, 'project')}, and{' '}
                {plural(openTasks, 'open task')} with their logged time? This cannot be
                undone.
              </p>
              <div className="flex shrink-0 gap-2">
                <Button variant="ghost" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  loading={remove.isPending}
                  onClick={() =>
                    remove.mutate(client.id, {
                      onSuccess: () => toast.show(`Deleted ${client.name}`),
                      onSettled: () => setConfirming(false),
                    })
                  }
                >
                  Delete
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={motionSafe(springDefault)}
            className="overflow-hidden"
          >
            <ul className="border-t border-border">
              {projects.length === 0 && (
                <li className="px-3 py-3 text-small text-text-tertiary">
                  No projects yet. Tasks can also sit directly under the client.
                </li>
              )}
              {projects.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  onEdit={() => onEditProject(project)}
                />
              ))}
              {!client.is_personal && (
                <li className="border-t border-border px-3 py-2 sm:hidden">
                  <Button
                    variant="ghost"
                    className="min-h-touch w-full justify-start text-danger"
                    onClick={() => setConfirming(true)}
                  >
                    <Trash2 size={15} strokeWidth={1.5} aria-hidden />
                    Delete {client.name}
                  </Button>
                </li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

function ProjectRow({ project, onEdit }: { project: Project; onEdit: () => void }) {
  const remove = useDeleteProject();
  const [confirming, setConfirming] = useState(false);

  return (
    <li className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 pl-9 last:border-0">
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onEdit}
          className="block truncate text-left text-body hover:text-accent"
        >
          {project.name}
        </button>
        <p className="mt-0.5 flex flex-wrap gap-3 text-small text-text-tertiary">
          <span>{project.status.replace('_', ' ')}</span>
          {project.default_billable && <span className="text-accent">Billable</span>}
          {project.weekly_time_budget_minutes && (
            <span className="tabular">
              {(project.weekly_time_budget_minutes / 60).toFixed(1)}h/week
            </span>
          )}
        </p>
      </div>

      {confirming ? (
        <div className="flex items-center gap-2">
          {/* Deleting a project keeps its tasks -- the server sets their
              project to null rather than destroying the work inside it. */}
          <span className="text-small text-text-secondary">
            Delete project? Its tasks stay under the client.
          </span>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() =>
              remove.mutate(project.id, {
                onSuccess: () => toast.show(`Deleted ${project.name}`),
                onSettled: () => setConfirming(false),
              })
            }
          >
            Delete
          </Button>
        </div>
      ) : (
        <div className="flex shrink-0 items-center gap-1">
          <IconButton onClick={onEdit} label={`Edit ${project.name}`}>
            <Pencil size={15} strokeWidth={1.5} aria-hidden />
          </IconButton>
          <IconButton onClick={() => setConfirming(true)} label={`Delete ${project.name}`} danger>
            <Trash2 size={15} strokeWidth={1.5} aria-hidden />
          </IconButton>
        </div>
      )}
    </li>
  );
}

/** `1 project` / `2 projects`. Cheap, and "1 projects" reads as a bug. */
function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function IconButton({
  onClick,
  label,
  danger,
  children,
}: {
  onClick: () => void;
  label: string;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'pressable flex size-action items-center justify-center rounded-full',
        'text-text-tertiary hover:bg-surface-3',
        danger ? 'hover:text-danger' : 'hover:text-text',
      )}
    >
      {children}
    </button>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-surface-1 p-5">
      <Users size={20} strokeWidth={1.5} className="text-text-tertiary" aria-hidden />
      <div>
        <p className="text-body font-medium">No clients yet</p>
        <p className="mt-1 text-small text-text-secondary">
          Add one for each person or company you do work for. Personal work already has a
          home.
        </p>
      </div>
      <Button variant="primary" onClick={onAdd}>
        <Plus size={15} strokeWidth={1.5} aria-hidden />
        New client
      </Button>
    </div>
  );
}
